





























































































import { Component, Vue } from 'vue-property-decorator';
import { mdbAlert, mdbBtn, mdbIcon, mdbTooltip, mdbRow, mdbCol } from '@folky-tools/mdbvue';
import { usersService, authorizationManagerService, referencesClassificationService, notificationsService } from '@/services';
import { Category } from '@/models/references-classification/category';
import { UserActionLogInfo } from '@/models/users/user-logging/user-action-log-info';
import { UserActionType } from '@/models/users/user-logging/user-action-type';
import { UserActionEntityType } from '@/models/users/user-logging/user-action-entity-type';
import { UserActionScopeType } from '@/models/users/user-logging/user-action-scope-type';
import Swal from 'sweetalert2';

@Component({
    components: {
        mdbAlert,
        mdbBtn,
        mdbIcon,
        mdbTooltip,
        mdbRow,
        mdbCol,
        FormModal: () => import('@/components/shared/FormModal.vue'),
        EditCategory: () => import('@/components/businesses/EditCategory.vue'),
        AppSysBar: () => import('@/components/shared/AppSysBar.vue')
    }
})
export default class BusinessCategoriesView extends Vue {
    private canEditBusiness = false;
    private categories: Category[] = [];
    private selectedCategory: Category = new Category();
    private selectedCategoryCopy: Category = new Category();

    private showEditCategory: boolean = false;
    private savingCategory: boolean = false;
    private pageContentLoaded: boolean = false;
    private isPageInfoVisible: boolean = false;

    private async created(): Promise<void> {
        this.$root.$emit('change-active-page', 'views.businesses.businessCategoriesView.title', this.$route.query.businessName ? this.$route.query.businessName : '...');
        this.canEditBusiness = authorizationManagerService.isAdmin || authorizationManagerService.authorizedBusinessIdsToEdit.some(id => id === +this.$route.params.id);
        this.categories = [...[new Category({ name: this.$i18n.t('views.businesses.businessCategoriesView.categories.root'), level: 0 })], ...(await referencesClassificationService.getCategories(+this.$route.params.id))];
    }

    private async mounted(): Promise<void> {
        await usersService.logUserAction(
            new UserActionLogInfo({
                type: UserActionType.HasVisited,
                entity: UserActionEntityType.Businesses,
                scope: UserActionScopeType.BusinessReferenceCategories,
                entityId: +this.$route.params.id
            })
        );
        this.pageContentLoaded = true;
    }

    private addCategory(): void {
        this.selectedCategory = new Category({ businessId: +this.$route.params.id });
        this.selectedCategoryCopy = this.selectedCategory.clone();
        this.showEditCategory = true;
    }

    private editCategory(category: Category): void {
        this.selectedCategory = category;
        this.selectedCategoryCopy = this.selectedCategory.clone();
        this.showEditCategory = true;
    }

    private async saveCategory(): Promise<void> {
        this.savingCategory = true;
        this.pageContentLoaded = false;

        if ((this.$refs.editCategory as any).hasErrors()) {
            this.savingCategory = false;
            this.pageContentLoaded = true;
            return;
        }

        try {
            if (this.selectedCategory.id > 0) {
                this.categories = [...[new Category({ name: this.$i18n.t('views.businesses.businessCategoriesView.categories.root'), level: 0 })], ...(await referencesClassificationService.updateCategory(this.selectedCategory))];

                this.selectedCategoryCopy = this.selectedCategory.clone();
                this.closeCategoryEdition();
                notificationsService.showToastSuccessNotification('views.businesses.businessCategoriesView.notifications.categorySavedSuccess.existingCategory');

                await usersService.logUserAction(
                    new UserActionLogInfo({
                        type: UserActionType.HasChanged,
                        entity: UserActionEntityType.Businesses,
                        scope: UserActionScopeType.BusinessReferenceCategories,
                        entityId: +this.$route.params.id
                    })
                );

                return;
            } else {
                this.categories = [...[new Category({ name: this.$i18n.t('views.businesses.businessCategoriesView.categories.root'), level: 0 })], ...(await referencesClassificationService.newCategory(this.selectedCategory))];

                this.selectedCategoryCopy = this.selectedCategory.clone();
                this.closeCategoryEdition();
                notificationsService.showToastSuccessNotification('views.businesses.businessCategoriesView.notifications.categorySavedSuccess.newCategory');

                await usersService.logUserAction(
                    new UserActionLogInfo({
                        type: UserActionType.HasCreated,
                        entity: UserActionEntityType.Businesses,
                        scope: UserActionScopeType.BusinessReferenceCategories,
                        entityId: +this.$route.params.id
                    })
                );

                return;
            }
        } catch {
            notificationsService.showToastErrorNotification();
        } finally {
            this.savingCategory = false;
            this.pageContentLoaded = true;
        }
    }

    private closeCategoryEdition(): void {
        this.selectedCategory.name = this.selectedCategoryCopy.name;
        this.selectedCategory.isQuickNavigation = this.selectedCategoryCopy.isQuickNavigation;
        this.selectedCategory.isQuickNavigationInherited = this.selectedCategoryCopy.isQuickNavigationInherited;
        this.selectedCategory.parentCategoryId = this.selectedCategoryCopy.parentCategoryId;
        this.selectedCategory.level = this.selectedCategoryCopy.level;
        this.showEditCategory = false;
    }

    private async deleteCategory(category: Category): Promise<void> {
        const questionResult = await Swal.fire({
            title: this.$i18n.t('views.businesses.businessCategoriesView.actions.delete.modal.title').toString(),
            html: '<h3>' + category.name + '</h3>' + this.$i18n.t('views.businesses.businessCategoriesView.actions.delete.modal.textHtml').toString(),
            type: 'warning',
            showCancelButton: true,
            reverseButtons: true,
            focusConfirm: false,
            focusCancel: true,
            confirmButtonText: this.$i18n.t('views.businesses.businessCategoriesView.actions.delete.modal.confirm.doConfirm').toString(),
            cancelButtonText: this.$i18n.t('views.businesses.businessCategoriesView.actions.delete.modal.doCancel').toString(),
            customClass: {
                confirmButton: 'btn btn-primary',
                cancelButton: 'btn btn-danger'
            },
            animation: false
        });

        if (questionResult.value) {
            this.pageContentLoaded = false;
            this.categories = [...[new Category({ name: this.$i18n.t('views.businesses.businessCategoriesView.categories.root'), level: 0 })], ...(await referencesClassificationService.disableCategory(category))];
            if (this.categories) {
                notificationsService.showToastSuccessNotification('views.businesses.businessCategoriesView.actions.delete.modal.confirm.successMessage');

                await usersService.logUserAction(
                    new UserActionLogInfo({
                        type: UserActionType.HasDeleted,
                        entity: UserActionEntityType.Businesses,
                        scope: UserActionScopeType.BusinessReferenceCategories,
                        entityId: +this.$route.params.id
                    })
                );

                this.pageContentLoaded = true;
                return;
            }

            this.pageContentLoaded = true;
            notificationsService.showToastErrorNotification();
        }
    }

    private async copyToClipboard(text: string): Promise<void> {
        await navigator.clipboard.writeText(text);
        notificationsService.showToastSuccessNotification('views.businesses.businessCategoriesView.notifications.urlCopiedSuccess');
    }
}
